import { useTenderActionError } from "../../../../../hooks/useTenderActionError";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import style from "./AviationExpeditorActionsTab.module.scss";
import { Aviation } from "../../../../../services";
import { TenderDeadlineText } from "../../../../shared/TenderDeadlineText/TenderDeadlineText";
import CustomInput from "../../../../custom/custom-input/CustomInput";
import CustomInputCurrency from "../../../../custom/custom-input-currency/CustomInputCurrency";
import CustomTextarea from "../../../../custom/custom-textarea/CustomTextarea";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import ConfirmActionModal from "../../../../orders-new/order-info-expeditor/components/ConfirmActionModal/ConfirmActionModal";

const AviationExpeditorBidForm = ({ onRefreshOrder, order, onUpdateData }) => {
  const { t } = useTranslation();
  const { requestErrorDisplay } = useTenderActionError(order, { onRefreshOrder });
  const previousOrderIdRef = useRef(order?.id);
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState(
    order?.acceptedCurrency || order?.currency || "EUR",
  );
  const [volume, setVolume] = useState("");
  const [weight, setWeight] = useState("");
  const [plannedDays, setPlannedDays] = useState("");
  const [comments, setComments] = useState("");
  const [priceError, setPriceError] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lockCurrency = Boolean(order?.acceptedCurrency);
  const isAuction = order?.assignmentMethod === "Auction";

  useEffect(() => {
    setCurrency(order?.acceptedCurrency || order?.currency || "EUR");

    if (previousOrderIdRef.current === order?.id) return;
    previousOrderIdRef.current = order?.id;
    setPrice("");
    setVolume("");
    setWeight("");
    setPlannedDays("");
    setComments("");
    setPriceError("");
    setIsConfirmOpen(false);
  }, [order?.acceptedCurrency, order?.currency, order?.id]);

  const handleSuggestClick = () => {
    const roundedPrice = Math.round(Number(price));

    if (!roundedPrice || roundedPrice <= 0) {
      setPriceError(t("orderInfoExpeditor.suggestPrice.priceRequired"));
      return;
    }
    if (roundedPrice > 2000000000) {
      const message = t("orderInfoExpeditor.suggestPrice.priceTooLarge");
      setPriceError(message);
      toast.error(message);
      return;
    }

    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!order?.id) return;

    const payload = {
      price: Math.round(Number(price)),
      currency,
      plannedDays,
      comments: comments.trim(),
      volume: volume === "" ? null : Number(volume),
      weight: weight === "" ? null : Number(weight),
      isExtraCosts: false,
    };

    try {
      setIsSubmitting(true);
      await Aviation.suggestedPrice(order.id, payload);
      toast.success(t("orderInfoExpeditor.suggestPrice.bidSent"));
      setPrice("");
      setVolume("");
      setWeight("");
      setPlannedDays("");
      setComments("");
      setIsConfirmOpen(false);
      onUpdateData?.();
    } catch (error) {
      console.error(error);
      await requestErrorDisplay(error, t, "orderInfoExpeditor.suggestPrice.sendBidError");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={style.card}>
      <div className={style.topbar}>
        <div className={style.heading}>
          <span className={isAuction ? `${style.topbar_title} ${style.auction_title}` : style.topbar_title}>
            {t("orderInfoExpeditor.suggestPrice.title")}
          </span>
          {isAuction && (
            <span className={style.auction_description}>
              {t("orderInfoExpeditor.suggestPrice.auctionDescription")}
            </span>
          )}
        </div>
        {order?.tenderDateEnd ? (
          <div className={style.tender_deadline}>
            <span className={style.tender_deadline_label}>
              {t(isAuction
                ? "orderInfoExpeditor.suggestPrice.auctionDeadline"
                : "orderInfoExpeditor.suggestPrice.tenderDeadline")}
            </span>
            <span className={style.tender_deadline_value}>
              <TenderDeadlineText date={order.tenderDateEnd} />
            </span>
          </div>
        ) : null}
      </div>

      <div className={style.inputs_row}>
        <div className={style.input_price}>
          <CustomInputCurrency
            value={price}
            isBgGray
            inputType="number"
            labelText={`${t("orderInfo.suggestPrice.yourPriceLabel")}*`}
            placeholder={t("orderInfo.suggestPrice.placeholder")}
            errorText={priceError}
            currency={currency}
            lockCurrency={lockCurrency}
            onChangeValue={(value) => {
              setPrice(value);
              setPriceError("");
            }}
            onChangeCurrency={setCurrency}
          />
        </div>
        <div className={style.input_secondary}>
          <CustomInput
            value={volume}
            isBgGray
            inputType="number"
            labelText={t("ordersCreateDesktop.detailsSection.volumeLabel")}
            placeholder={
              order?.volume != null
                ? `${order.volume} ${t("orderInfoExpeditor.suggestPrice.cubicMeter")}`
                : t("orderInfoExpeditor.suggestPrice.cubicMeter")
            }
            onChangeValue={setVolume}
          />
        </div>
        <div className={style.input_secondary}>
          <CustomInput
            value={weight}
            isBgGray
            inputType="number"
            labelText={t("orderInfo.ordersInfoCargo.massLabel")}
            placeholder={
              order?.weight != null
                ? `${order.weight} ${t("orderInfo.ordersInfoCargo.kgUnit")}`
                : t("orderInfo.ordersInfoCargo.kgUnit")
            }
            onChangeValue={setWeight}
          />
        </div>
      </div>

      {lockCurrency ? (
        <span className={style.currency_hint}>
          {t("orderInfoExpeditor.suggestPrice.currencyLockedHint")}
        </span>
      ) : null}

      <div className={style.inputs_row}>
        <CustomInput
          value={plannedDays}
          isBgGray
          inputType="text"
          labelText={t("orderInfoExpeditor.suggestPrice.transitDaysLabel")}
          placeholder={t("orderInfoExpeditor.suggestPrice.transitDaysPlaceholder")}
          onChangeValue={(value) => setPlannedDays(value.replace(/[^0-9-]/g, ""))}
        />
      </div>

      <div className={style.inputs_row}>
        <CustomTextarea
          value={comments}
          isBgGray
          rows={1}
          labelText={t("orderInfo.suggestPrice.commentLabel")}
          placeholder={t("orderInfoExpeditor.suggestPrice.commentPlaceholder")}
          onChangeValue={setComments}
        />
      </div>

      <div className={style.actions_row_full}>
        <CustomButton
          text={t("orderInfo.suggestPrice.suggestButton")}
          isNavy
          onClick={handleSuggestClick}
          isLoading={isSubmitting}
          disabled={isSubmitting}
          style={{ width: "100%" }}
        />
      </div>

      <ConfirmActionModal
        isOpen={isConfirmOpen}
        title={t("orderInfoExpeditor.suggestPrice.submitTitle")}
        message={t(isAuction
          ? "orderInfoExpeditor.suggestPrice.auctionSubmitDescription"
          : "orderInfoExpeditor.suggestPrice.submitDescription")}
        confirmText={t("orderInfoExpeditor.suggestPrice.yes")}
        cancelText={t("orderInfoExpeditor.suggestPrice.no")}
        onConfirm={handleConfirm}
        onCancel={() => setIsConfirmOpen(false)}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default AviationExpeditorBidForm;
